import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Upload, LayoutGrid, Activity, LogOut } from "lucide-react";

const NAV_ITEMS = [
  { to: "/", icon: Upload, label: "Upload" },
  { to: "/history", icon: LayoutGrid, label: "History" },
];

export default function Sidebar({ user }) {
  const navigate = useNavigate();

  function handleLogout() {
    fetch("/auth/logout", { credentials: "include" }).finally(() => {
      navigate("/login", { replace: true });
    });
  }

  return (
    <aside
      style={{
        width: 220,
        minWidth: 220,
        background: "var(--ink-900)",
        borderRight: "1px solid var(--slate-border)",
        display: "flex",
        flexDirection: "column",
        padding: "28px 0",
        height: "100vh",
        position: "sticky",
        top: 0,
      }}
    >
      {/* Logo */}
      <div style={{ padding: "0 24px 32px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              background: "var(--acid)",
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Activity size={16} color="var(--ink-950)" strokeWidth={2.5} />
          </div>
          <div>
            <div
              style={{
                fontFamily: "Syne, sans-serif",
                fontWeight: 700,
                fontSize: 15,
                color: "#E8EDF3",
                lineHeight: 1.1,
              }}
            >
              CV Parser
            </div>
            <div
              style={{
                fontFamily: "JetBrains Mono, monospace",
                fontSize: 10,
                color: "var(--slate-muted)",
                letterSpacing: "0.08em",
              }}
            >
              be-exec.com
            </div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: "0 12px" }}>
        <div
          style={{
            fontFamily: "JetBrains Mono, monospace",
            fontSize: 9,
            letterSpacing: "0.12em",
            color: "var(--slate-muted)",
            textTransform: "uppercase",
            padding: "0 12px",
            marginBottom: 8,
          }}
        >
          Navigation
        </div>
        {NAV_ITEMS.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            style={({ isActive }) => ({
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "9px 12px",
              borderRadius: 8,
              textDecoration: "none",
              fontFamily: "DM Sans, sans-serif",
              fontWeight: isActive ? 500 : 400,
              fontSize: 14,
              color: isActive ? "var(--acid)" : "var(--slate-text)",
              background: isActive ? "rgba(200,241,53,0.08)" : "transparent",
              border: isActive ? "1px solid rgba(200,241,53,0.15)" : "1px solid transparent",
              marginBottom: 4,
              transition: "all 0.15s ease",
            })}
          >
            <Icon size={15} strokeWidth={isActive => isActive ? 2.5 : 1.8} />
            {label}
          </NavLink>
        ))}
      </nav>

      {/* User info + logout */}
      {user && (
        <div
          style={{
            margin: "0 24px 16px",
            padding: "12px 14px",
            background: "var(--ink-800)",
            borderRadius: 8,
            border: "1px solid var(--slate-border)",
          }}
        >
          <div
            style={{
              fontFamily: "DM Sans, sans-serif",
              fontSize: 13,
              color: "#E8EDF3",
              fontWeight: 500,
              marginBottom: 2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {user.name || user.email}
          </div>
          {user.name && (
            <div
              style={{
                fontFamily: "JetBrains Mono, monospace",
                fontSize: 10,
                color: "var(--slate-muted)",
                marginBottom: 10,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {user.email}
            </div>
          )}
          <button
            onClick={handleLogout}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: "transparent",
              border: "1px solid var(--slate-border)",
              borderRadius: 6,
              color: "var(--slate-muted)",
              fontFamily: "DM Sans, sans-serif",
              fontSize: 12,
              cursor: "pointer",
              padding: "5px 10px",
              width: "100%",
              transition: "all 0.15s ease",
            }}
            onMouseEnter={e => {
              e.currentTarget.style.color = "#E8EDF3";
              e.currentTarget.style.borderColor = "#E8EDF3";
            }}
            onMouseLeave={e => {
              e.currentTarget.style.color = "var(--slate-muted)";
              e.currentTarget.style.borderColor = "var(--slate-border)";
            }}
          >
            <LogOut size={12} />
            Выйти
          </button>
        </div>
      )}

      {/* Status indicator */}
      <div
        style={{
          margin: "0 24px",
          padding: "12px 14px",
          background: "var(--ink-800)",
          borderRadius: 8,
          border: "1px solid var(--slate-border)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            marginBottom: 4,
          }}
        >
          <div
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: "#4ADE80",
              boxShadow: "0 0 6px rgba(74,222,128,0.6)",
              animation: "pulse 2s infinite",
            }}
          />
          <span
            style={{
              fontFamily: "JetBrains Mono, monospace",
              fontSize: 10,
              color: "#4ADE80",
              letterSpacing: "0.04em",
            }}
          >
            API Live
          </span>
        </div>
        <div
          style={{
            fontFamily: "JetBrains Mono, monospace",
            fontSize: 9,
            color: "var(--slate-muted)",
            letterSpacing: "0.02em",
          }}
        >
          GPT-4o · FastAPI
        </div>
      </div>
    </aside>
  );
}
